import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshCw, Mail, Inbox, Clock } from "lucide-react";
import { MaskedEmailCode } from "@/components/MaskedCode";

interface Email {
  uid: number;
  from: string;
  subject: string;
  date: string;
  body: string;
}

type ReadEmailsV2Item = {
  id: number;
  created_at: string;
  subject?: string | null;
  from?: string | null;
  body?: string | null;
};

export function LoasTab() {
  const { user } = useAuth();
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const { toast } = useToast();

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("read-emails-v2", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const raw = (data?.data ?? []) as ReadEmailsV2Item[];
      setEmails(
        raw.slice(0, 10).map((item) => ({
          uid: item.id,
          from: item.from ?? "",
          subject: item.subject ?? "",
          // read-emails-v2 returns created_at; use as received time
          date: item.created_at,
          body: item.body ?? "",
        })),
      );
      setLastFetched(new Date());
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Erro ao buscar emails",
        description: err.message || "Não foi possível carregar os emails.",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    // Dev-only: allow forcing mock data to validate UI quickly
    if (import.meta.env.DEV) {
      const params = new URLSearchParams(window.location.search);
      if (params.get("mockEmails") === "1") {
        setEmails([
          {
            uid: 1,
            from: "no-reply@loas.example",
            subject: "Email de teste (mock)",
            date: new Date().toISOString(),
            body: "Este é um email simulado para validar a exibição do horário de recebimento.",
          },
        ]);
        setLastFetched(new Date());
        return;
      }
    }

    fetchEmails();
  }, [fetchEmails]);

  const safeDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateStr: string) => {
    try {
      const d = safeDate(dateStr);
      if (!d) return "—";
      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    } catch {
      return "—";
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground tracking-tight">
            Caixa de Entrada — LOAS
          </h2>
          {lastFetched && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Atualizado em {formatDate(lastFetched.toISOString())}
            </p>
          )}
        </div>
        <Button
          onClick={fetchEmails}
          disabled={loading}
          variant="default"
          className=" bg-secondary border-border text-foreground hover:text-foreground hover:bg-accent hover:border-border gap-2 transition-all"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Carregando..." : "Recarregar"}
        </Button>
      </div>

      {/* Loading state (initial) */}
      {loading && emails.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
          <RefreshCw className="h-8 w-8 animate-spin mb-4" />
          <p className="text-sm">Buscando emails...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && emails.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
          <Inbox className="h-12 w-12 mb-4 opacity-40" />
          <p className="text-base font-medium text-muted-foreground">
            Nenhum email encontrado
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Tente recarregar para buscar novamente.
          </p>
        </div>
      )}

      {/* Email list */}
      {emails.length > 0 && (
        <div className="space-y-3">
          {emails.map((email) => (
            <div
              key={email.uid}
              className="group bg-card border border-border rounded-xl overflow-hidden hover:border-border transition-all duration-200"
            >
              {/* Email header */}
              <div className="flex items-center gap-4 px-4 py-3 border-b border-border group-hover:border-border transition-colors">

                {/* Date */}

                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {formatDate(email.date)}
                  </div>
                </div>
              </div>

              {/* Subject + body (ambos mascarados até revelar) */}
              <div className="px-4 py-3">
                {user ? (
                  <MaskedEmailCode
                    body={email.body}
                    subject={email.subject}
                    emailUid={email.uid}
                    user={{ id: user.id, email: user.email ?? "" }}
                    className="text-sm text-muted-foreground leading-relaxed"
                  />
                ) : (
                  <>
                    <div className="flex items-start gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-muted-foreground select-none blur-md">
                        {email.subject}
                      </p>
                    </div>
                    <div className="pl-6">
                      <p className="text-sm text-muted-foreground select-none blur-md">
                        {email.body}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
